import React, { useEffect, useMemo, useRef, useState } from 'react';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Marker, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { EIconTypes } from 'constants/Icons';
import { EButtonVariants } from 'constants/Buttons';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import MapLegalityDropdown from 'pages/Firearms/RegisteredFirearmsMapPage/MapLegalityDropdown';
import { useDictionary } from 'models/dictionaryContext';
import FirearmDataSidebar from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmDataSidebar';
import { useLang } from 'models/langContext';
import { LegalityTypes } from 'constants/LegalityTypes';
import SearchFilterStore from 'state/SearchFilterStore';
import {
  EAssetType,
  ILegalEntityAddressDetailAccessories,
  ILegalEntityAddressDetailAmmunition,
  ILegalEntityAddressDetailComponents,
  ILegalEntityAddressDetailFirearms,
} from 'models/legalEntity';
import AccessoryDataSidebar from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/AncillaryDataSidebar';
import AmmunitionDataSidebar from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/AmmunitionDataSidebar';
import ComponentDataSidebar from 'pages/AncillariesPage/AncillariesMap/AncillariesLocationMap/ComponentDataSidebar';
import {
  ButtonFullScreen,
  ButtonFullScreenExit,
  ButtonZoomIn,
  ButtonZoomOut,
  StyledLocationMapWrapper,
  StyledMapContainer,
} from './style';
import { EMarkerType, MarkerIcons } from './helper';
import AllDataSidebar from './AllDataSidebar';
import { IAccessory, IAmmunition, IComponent } from 'models/ancillaries';
import { MAP_MAXIMUM_CLUSTER_SIZE_FOR_SPIDERLY } from 'constants/Map';

const MarkerIcon = (isLegal: boolean, type: EMarkerType) =>
  L.icon({
    iconUrl: isLegal ? MarkerIcons(type).Legal : MarkerIcons(type).Illegal,
    iconSize: [32, 39],
    iconAnchor: [16, 39],
    popupAnchor: [0, 0],
  });

interface ILocationMap {
  firearms: ILegalEntityAddressDetailFirearms[];
  accessories: ILegalEntityAddressDetailAccessories[];
  ammunition: ILegalEntityAddressDetailAmmunition[];
  components: ILegalEntityAddressDetailComponents[];
  assetTypeSelected: EAssetType | null;
  showLegalityBar: boolean;
  defaultLegality?: string | undefined;
  isOnline: boolean;
  latitude?: number;
  longitude?: number;

  updateLegality(id: string): void;
}

const LocationMap = ({
  firearms,
  accessories,
  ammunition,
  components,
  assetTypeSelected,
  updateLegality,
  defaultLegality = '',
  showLegalityBar = true,
  latitude,
  longitude,
  isOnline,
}: ILocationMap) => {
  (window as any).xFirearms = firearms; // fix for leaflet event function/no access to props
  (window as any).xAccessories = accessories; // fix for leaflet event function/no access to props
  (window as any).xAmmunition = ammunition; // fix for leaflet event function/no access to props
  (window as any).xComponents = components; // fix for leaflet event function/no access to props

  useEffect(() => {
    const list = {
      firearms: (window as any).xFirearms,
      accessories: (window as any).xAccessories,
      ammunition: (window as any).xAmmunition,
      components: (window as any).xComponents,
    };
    SearchFilterStore.setAssets(list);
  }, [firearms, accessories, ammunition, components]);

  const { legality, getLegality } = useDictionary();
  const mapRef = useRef<L.Map | null>(null);
  const [fullScreenState, setFullScreenState] = useState(false);
  const [showAllDataSidebar, setShowAllDataSidebar] = useState(true);
  const legalityRef = useRef('');
  const assetTypeSelectedRef = useRef(assetTypeSelected);
  useEffect(() => {
    assetTypeSelectedRef.current = assetTypeSelected;
  }, [assetTypeSelected]);
  const { selectedLanguage } = useLang();

  const url = isOnline
    ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    : `${process.env.REACT_APP_MAP_TILE_URL}tile/{z}/{x}/{y}.png`;

  useEffect(() => {
    (async function init() {
      legalityRef.current = defaultLegality;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function initDictionaries() {
      await getLegality();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const ChangeView = () => {
    const map = useMap();
    if (map) {
      mapRef.current = map;
    }
    const bounds: [number, number][] = [];
    if (latitude && longitude) {
      bounds.push([latitude, longitude]);
    }
    if (bounds.length) {
      map.fitBounds(bounds);
    }
    if (map.getZoom() > 10) {
      map.setZoom(10);
    }
    return null;
  };

  const zoomMap = (isZoomIn: boolean) => {
    if (mapRef.current) {
      mapRef.current.setZoom(
        isZoomIn ? mapRef.current.getZoom() + 1 : mapRef.current.getZoom() - 1,
      );
    }
  };

  const toggleFullScreen = () => {
    if (mapRef.current) {
      const current = mapRef.current as any;
      current.toggleFullscreen();
      setFullScreenState((prevState) => !prevState);
    }
  };

  const updateCurrentLegality = (value: string) => {
    legalityRef.current = value;
    updateLegality(value);
  };

  const getClusterClassName = () => {
    if (!legalityRef.current) {
      return '';
    }
    return legalityRef.current === LegalityTypes.LEGAL
      ? 'custom-marker-cluster-legal'
      : 'custom-marker-cluster-illegal';
  };

  const closeDataSidebar = () => {
    SearchFilterStore.clearAll();
  };

  const showAccessoriesDataSidebar = (markers: any[]) => {
    const accessoriesIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const accessoriesList = (window as any).xAccessories;
    SearchFilterStore.setAccessories(
      accessoriesList.filter((accessory: IAccessory) =>
        accessoriesIds.has(accessory.uniqueId),
      ),
    );
  };

  const showAmmunitionDataSidebar = (markers: any[]) => {
    const ammunitionIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const ammunitionList = (window as any).xAmmunition;
    SearchFilterStore.setAmmunition(
      ammunitionList.filter((singleAmmunition: IAmmunition) =>
        ammunitionIds.has(singleAmmunition.uniqueId),
      ),
    );
  };

  const showComponentsDataSidebar = (markers: any[]) => {
    const componentIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const componentsList = (window as any).xComponents;
    SearchFilterStore.setComponents(
      componentsList.filter((component: IComponent) => componentIds.has(component.uniqueId)),
    );
  };

  const showFirearmDataSidebar = (markers: any[]) => {
    const firearmsIds = new Set(markers.map((marker) => marker.options.alt.split(',')[0]));
    const firearmsList = (window as any).xFirearms;
    SearchFilterStore.setFirearms(
      firearmsList.filter((firearm: any) => firearmsIds.has(firearm.uniqueId)),
    );
  };

  const createClusterCustomIcon = (cluster: any) =>
    L.divIcon({
      html: `<span>${cluster.getChildCount()}</span>`,
      className: `custom-marker-cluster ${getClusterClassName()}`,
      iconSize: L.point(56, 56, true),
    });

  const showsDataSidebar = (type: EMarkerType, arg: any) => {
    switch (type) {
      case EMarkerType.firearms:
        showFirearmDataSidebar(arg);
        break;
      case EMarkerType.accessories:
        showAccessoriesDataSidebar(arg);
        break;
      case EMarkerType.components:
        showComponentsDataSidebar(arg);
        break;
      case EMarkerType.ammunition:
        showAmmunitionDataSidebar(arg);
        break;
      default:
        break;
    }
  };

  const renderMarker = (item: any, type: EMarkerType) => (
    <Marker
      key={item.uniqueId}
      alt={`${item.uniqueId},${item.legalityUniqueId}`}
      position={[latitude ?? 0, longitude ?? 0]}
      icon={MarkerIcon(item.legalityUniqueId === LegalityTypes.LEGAL, type)}
      eventHandlers={{
        click: (e) => {
          showsDataSidebar(type, [e.target]);
        },
        mouseover: (e) => {
          e.target.setIcon(
            L.icon({
              iconUrl:
                e.target.options.alt.split(',')[1] === LegalityTypes.LEGAL
                  ? MarkerIcons(type).LegalHover
                  : MarkerIcons(type).IllegalHover,
              iconSize: [32, 39],
              iconAnchor: [16, 39],
              popupAnchor: [0, 0],
            }),
          );
        },
        mouseout: (e) => {
          e.target.setIcon(
            L.icon({
              iconUrl:
                e.target.options.alt.split(',')[1] === LegalityTypes.LEGAL
                  ? MarkerIcons(type).Legal
                  : MarkerIcons(type).Illegal,
              iconSize: [32, 39],
              iconAnchor: [16, 39],
              popupAnchor: [0, 0],
            }),
          );
        },
      }}
    />
  );

  const markers = useMemo(
    () => (
      <>
        {firearms.map(
          (firearm) => latitude && longitude && renderMarker(firearm, EMarkerType.firearms),
        )}
        {accessories.map(
          (accessory) =>
            latitude && longitude && renderMarker(accessory, EMarkerType.accessories),
        )}
        {ammunition.map(
          (ammunitionItem) =>
            latitude && longitude && renderMarker(ammunitionItem, EMarkerType.ammunition),
        )}
        {components.map(
          (component) =>
            latitude && longitude && renderMarker(component, EMarkerType.components),
        )}
      </>
    ),

    [firearms, accessories, ammunition, components],
  );

  return (
    <StyledLocationMapWrapper>
      {showAllDataSidebar && (
        <AllDataSidebar
          handleClose={() => {
            closeDataSidebar();
            setShowAllDataSidebar(false);
          }}
        />
      )}
      <FirearmDataSidebar
        width="370px"
        handleBack={(show: boolean = true) => setShowAllDataSidebar(show)}
        title="assets.listFirearmsGroup.label"
      />
      <AccessoryDataSidebar
        width="370px"
        handleBack={(show: boolean = true) => setShowAllDataSidebar(show)}
        title="assets.listAccessoryGroup.label"
      />
      <AmmunitionDataSidebar
        width="370px"
        handleBack={(show: boolean = true) => setShowAllDataSidebar(show)}
        title="assets.listAmmunitionGroup.label"
      />
      <ComponentDataSidebar
        width="370px"
        handleBack={(show: boolean = true) => setShowAllDataSidebar(show)}
        title="assets.listComponentGroup.label"
      />
      <StyledMapContainer scrollWheelZoom zoomControl={false}>
        {showLegalityBar && (
          <MapLegalityDropdown
            updateLegality={updateCurrentLegality}
            legality={legality}
            label="assets.location.legality.label"
          />
        )}
        <ChangeView />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={url}
        />
        <MarkerClusterGroup
          showCoverageOnHover={false}
          spiderfyOnMaxZoom={false}
          zoomToBoundsOnClick={false}
          iconCreateFunction={createClusterCustomIcon}
          onClick={(e: any) => {
            const cluster = e.layer;
            const markers = cluster.getAllChildMarkers();

            const group = cluster._group;
            let bottom = cluster;

            while (bottom._childClusters.length === 1) {
              bottom = bottom._childClusters[0];
            }

            const isAtMaxZoomAndSameCount =
              bottom._zoom === group._maxZoom && bottom._childCount === cluster._childCount;

            if (isAtMaxZoomAndSameCount) {
              if (
                cluster.getAllChildMarkers().length <= MAP_MAXIMUM_CLUSTER_SIZE_FOR_SPIDERLY
              ) {
                cluster.spiderfy();
              }
            } else {
              cluster.zoomToBounds();
            }

            const currentAssetType = assetTypeSelectedRef.current;
            if (!currentAssetType) return;

            switch (currentAssetType) {
              case EAssetType.firearms: {
                showsDataSidebar(EMarkerType.firearms, markers);
                break;
              }
              case EAssetType.accessories: {
                showsDataSidebar(EMarkerType.accessories, markers);
                break;
              }
              case EAssetType.ammunition: {
                showsDataSidebar(EMarkerType.ammunition, markers);
                break;
              }
              case EAssetType.components: {
                showsDataSidebar(EMarkerType.components, markers);
                break;
              }
            }
          }}
        >
          {markers !== null && markers}
        </MarkerClusterGroup>
        <ButtonZoomIn
          icon={EIconTypes.plus1}
          variant={EButtonVariants.outlined}
          onClick={() => zoomMap(true)}
        />
        <ButtonZoomOut
          icon={EIconTypes.minus}
          variant={EButtonVariants.outlined}
          onClick={() => zoomMap(false)}
        />
        {!fullScreenState && (
          <ButtonFullScreen
            icon={fullScreenState ? EIconTypes.fullScreenExit : EIconTypes.fullScreen}
            variant={EButtonVariants.outlined}
            onClick={() => toggleFullScreen()}
          />
        )}
        {fullScreenState && (
          <ButtonFullScreenExit
            icon={EIconTypes.fullScreenExit}
            variant={EButtonVariants.outlined}
            onClick={() => toggleFullScreen()}
          />
        )}
      </StyledMapContainer>
    </StyledLocationMapWrapper>
  );
};

export { LocationMap };
